/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  TypesenseCollectionApiKeyPair,
  TypesenseCollectionName,
  TypesenseKeyName,
  TypesenseHighlight,
  TypesenseHighlightItem,
} from './types';
import { typesense } from './typesenseClient';

export interface TypesenseDocument {
  id?: string;
  [key: string]: any;
}

interface SearchResult {
  hits?: Array<{
    document: TypesenseDocument;
    highlight?: TypesenseHighlight;
    highlights?: TypesenseHighlightItem[];
  }>;
  found: number;
  out_of: number;
  page: number;
  request_params: any;
  search_time_ms: number;
}

// Generic function to get filter options from any Typesense collection
export const getFilterOptions = async (
  collectionName: TypesenseCollectionName,
  valueField: string, // Field to use as the option value
  apiKey: string,
  labelField?: string, // Field to use as the option label (defaults to valueField)
  searchQuery: string = '*',
  limit: number = 100,
) => {
  try {
    const searchRequest = {
      searches: [
        {
          collection: collectionName,
          'x-typesense-api-key': apiKey,
          q: searchQuery,
          query_by: labelField || valueField,
          per_page: limit,
          page: 1,
        },
      ],
    };

    const multiSearchResults =
      await typesense.multiSearch.perform(searchRequest);
    const results: SearchResult = multiSearchResults.results[0];

    const options = (
      results.hits?.map((hit) => ({
        value: hit.document[valueField],
        label: hit.document[labelField || valueField],
        // Include full document in case you need other fields
        document: hit.document,
      })) || []
    ).filter((option) => option.value != null); // Filter out null/undefined values

    return {
      success: true,
      options,
      totalFound: results.found,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      options: [],
      totalFound: 0,
    };
  }
};

// Generic function to search any Typesense collection with pagination
export const searchCollection = async (
  collectionName: TypesenseCollectionName,
  queryByField: string,
  query: string = '*',
  apiKey: string,
  page: number = 1,
  perPage: number = 50,
  filterBy?: string,
  sortBy?: string,
) => {
  try {
    const searchRequest = {
      searches: [
        {
          collection: collectionName,
          'x-typesense-api-key': apiKey,
          q: query,
          query_by: queryByField,
          per_page: perPage,
          page: page,
          ...(filterBy && { filter_by: filterBy }),
          ...(sortBy && { sort_by: sortBy }),
        },
      ],
    };

    const multiSearchResults =
      await typesense.multiSearch.perform(searchRequest);
    const results: SearchResult = multiSearchResults.results[0];

    const documents = results.hits?.map((hit) => hit.document) || [];
    const hasMore = results.found > page * perPage;

    return {
      success: true,
      documents,
      pagination: {
        currentPage: page,
        perPage,
        totalFound: results.found,
        totalPages: Math.ceil(results.found / perPage),
        hasMore,
      },
      searchTimeMs: results.search_time_ms,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      documents: [],
      pagination: {
        currentPage: page,
        perPage,
        totalFound: 0,
        totalPages: 0,
        hasMore: false,
      },
      searchTimeMs: 0,
    };
  }
};

// Generic function to union search Typesense collections with pagination
export const searchCollections = async (
  collectionKeyPairs: TypesenseCollectionApiKeyPair[],
  queryByField: string,
  query: string = '*',
  page: number = 1,
  perPage: number = 50,
  filterByPerCollection?: Partial<Record<TypesenseKeyName, string>>,
  sortBy?: string,
) => {
  try {
    const searchRequests = {
      searches: collectionKeyPairs.map(
        ({ collectionName, keyName, apiKey }) => ({
          collection: collectionName,
          query_by: queryByField,
          'x-typesense-api-key': apiKey,
          q: query,
          ...(filterByPerCollection?.[keyName] && {
            filter_by: filterByPerCollection[keyName],
          }),
          ...(sortBy && { sort_by: sortBy }),
        }),
      ),
    };

    const multiSearchResults = await typesense.multiSearch.perform(
      {
        // @ts-expect-error: union is still new in typesense client
        union: true,
        ...(searchRequests as unknown as Parameters<
          typeof typesense.multiSearch.perform
        >[0]),
      },
      // page and per_page must be URL query string params for union multi-search
      { page, per_page: perPage, remove_duplicates: true },
    );

    const documents = multiSearchResults.hits?.map((hit) => hit.document) || [];
    const hasMore = multiSearchResults.found > page * perPage;

    return {
      success: true,
      documents,
      pagination: {
        currentPage: page,
        perPage,
        totalFound: multiSearchResults.found,
        totalPages: Math.ceil(multiSearchResults.found / perPage),
        hasMore,
      },
      searchTimeMs: multiSearchResults.search_time_ms,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      documents: [],
      pagination: {
        currentPage: page,
        perPage,
        totalFound: 0,
        totalPages: 0,
        hasMore: false,
      },
      searchTimeMs: 0,
    };
  }
};
